import { FunctionComponent, memo, useMemo } from "react";
import { StickyHeader } from "@ppb/the-wall-web";
import {
  HEADER_CONTAINER_ID,
  HEADER_SPACE_ID,
} from "@ppb/the-wall-web/components/bricks/StickyHeader/StickyHeader.types";
import EventSwitcherCard from "../components/EventSwitcherCard/view/EventSwitcherCard.web";
import type { ScoreboardSlot } from "../scoreboard-slots.web";
import type { EventViewData } from "../../../viewmodel/EventView.viewmodel";
import styles from "./EventViewHeader.web.module.css";

/**
 * Event View header (RaceMeetingView pattern): competition switcher + the sport's scoreboard slot, urn-driven
 * off the entity `<EventView>`'s query already wrote, so it paints on the first commit. No slot, no scoreboard
 * surface; no bell on web. `StickyHeader` sits *inside* it (`event-view-apollo-header-migration.md` §13.4.8).
 */
const EventViewHeader: FunctionComponent<{ data: EventViewData; slot?: ScoreboardSlot }> = ({ data, slot }) => {
  const { fixture } = data;

  // Web has no compiler (AGENTS.md): memoised on the viewmodel's stable `{ kind, urn }` so a sibling
  // or label update doesn't rebuild StickyHeader's two children.
  const scoreboard = useMemo(
    () => slot && { sticky: slot.renderSmall(fixture), inline: slot.renderDefault(fixture) },
    [slot, fixture],
  );

  return (
    <>
      <EventSwitcherCard
        viewUrn={data.urn}
        currentViewUrn={data.urn}
        currentLabel={data.label}
        siblings={data.siblings}
        headerTheming={data.headerTheming}
      />
      {scoreboard && (
        <div className={styles.scoreboard}>
          <StickyHeader
            stickyView={scoreboard.sticky}
            attachContainerId={HEADER_CONTAINER_ID}
            attachSpaceId={HEADER_SPACE_ID}
          >
            {scoreboard.inline}
          </StickyHeader>
        </div>
      )}
    </>
  );
};

export default memo(EventViewHeader);
